import moment, { Moment } from "moment-timezone";
import {
	BookingTableWithJoin,
	TableBookingBlockLike,
	blockToIntervalMs,
	buildBlockedByTableFromBlocks,
	buildOccupiedByTableForAssignment,
	dineInTableCapacity,
	filterTableBlocksForDay,
	isBookingIntervalWithinOpenHours,
	listMoveTableOptions,
	tableBlockedForWindow,
	validateExplicitTableAssignment,
	csvText,
	FORMATS,
	formatBookingTableLabel,
	liveTableDocketTableHeadline,
} from "@lib/common";

export { formatBookingTableLabel, liveTableDocketTableHeadline };

export const DEFAULT_TIMELINE_START = 9;
export const DEFAULT_TIMELINE_END = 23;
/** Default block width when booking has no stored duration (matches cloudtable prototype turn default). */
export const DEFAULT_BLOCK_DURATION_MINS = 90;

/**
 * Cloudtable-style floor grid: rows need ~80–100px so the table id + seat line can breathe.
 * (Our previous floor was ~56px because laneHeight 44 × 1 + 8 clamped to 56.)
 */
export const MIN_TABLE_ROW_PX = 92;
/** Vertical space per stacked booking lane (booking chip height ≈ this − 4). */
export const BOOKING_LANE_HEIGHT_PX = 56;
/** Table column width — must match `CornerCell` / `RowLabel` and `tableColW` in render. */
export const TABLE_COL_WIDTH_NARROW_PX = 118;
export const TABLE_COL_WIDTH_WIDE_PX = 142;

export function getTimelineBounds(
	r: T.Schema.Restaurant.RestaurantSchema,
	day: Moment,
	tz: string
): { startHour: number; endHour: number } {
	const weekday = day.clone().tz(tz).locale("en").format("dddd") as T.DaysOfWeek;
	const custom = r.settings.services?.table_booking?.hours;
	const useServiceHours =
		custom && Array.isArray(custom) && custom.length > 0;
	const slots = useServiceHours
		? custom!.filter((s) => s.day === weekday)
		: (r.location.opening_hours || []).filter((s) => s.day === weekday);
	if (!slots.length) {
		return { startHour: DEFAULT_TIMELINE_START, endHour: DEFAULT_TIMELINE_END };
	}
	let startMin = 24 * 60;
	let endMin = 0;
	for (const s of slots) {
		if (s.h24) {
			return { startHour: 0, endHour: 24 };
		}
		const open = s.open || "09:00";
		const close = s.close || "22:00";
		const [oh, om] = open.split(":").map((x) => parseInt(x, 10) || 0);
		const [ch, cm] = close.split(":").map((x) => parseInt(x, 10) || 0);
		const o = oh * 60 + om;
		let c = ch * 60 + cm;
		if (c <= o) c += 24 * 60;
		startMin = Math.min(startMin, o);
		endMin = Math.max(endMin, c);
	}
	const startHour = Math.max(0, Math.floor(startMin / 60));
	const endHour = Math.min(24, Math.ceil(endMin / 60));
	if (endHour <= startHour) {
		return { startHour: DEFAULT_TIMELINE_START, endHour: DEFAULT_TIMELINE_END };
	}
	return { startHour, endHour };
}

/** Matches store “Time interval (minutes)” for table booking (`times.conditions.time_interval`). */
/** Scroll timeline to first booking, or now on today when empty, else start of day. */
export function computeTimelineScrollLeft(args: {
	startHour: number;
	endHour: number;
	minuteSize: number;
	tableColW: number;
	viewportWidth: number;
	day: Moment;
	tz: string;
	items: Array<{ status: string; config: { timestamp: number } }>;
	scrollToNowWhenEmpty: boolean;
}): number {
	const {
		startHour,
		endHour,
		minuteSize,
		tableColW,
		viewportWidth,
		day,
		tz,
		items,
		scrollToNowWhenEmpty,
	} = args;
	const totalMinutes = Math.max(1, (endHour - startHour) * 60);
	const active = items.filter(
		(b) => b.status !== "cancelled" && b.status !== "complete"
	);

	let minsSinceStart: number;
	if (active.length) {
		const earliest = Math.min(
			...active.map((b) => moment(b.config.timestamp).tz(tz).valueOf())
		);
		const m = moment(earliest).tz(tz);
		minsSinceStart = m.hours() * 60 + m.minutes() - startHour * 60;
	} else if (scrollToNowWhenEmpty) {
		const now = moment().tz(tz);
		minsSinceStart = now.hours() * 60 + now.minutes() - startHour * 60;
	} else {
		minsSinceStart = 0;
	}

	minsSinceStart = Math.max(0, Math.min(totalMinutes, minsSinceStart));
	const targetPx = tableColW + minsSinceStart * minuteSize;
	const padding = 48;
	let scrollLeft = targetPx - tableColW - padding;
	scrollLeft = Math.max(0, scrollLeft);
	const contentWidth = tableColW + totalMinutes * minuteSize;
	const maxScroll = Math.max(0, contentWidth - viewportWidth);
	return Math.min(scrollLeft, maxScroll);
}

export function getTableBookingSlotIntervalMinutes(
	r: T.Schema.Restaurant.RestaurantSchema
): number {
	const raw =
		r.settings.services?.table_booking?.times?.conditions?.time_interval;
	const n = typeof raw === "number" ? raw : parseInt(String(raw ?? ""), 10);
	if (!Number.isFinite(n) || n < 1) {
		return 15;
	}
	return Math.floor(n);
}

export function csvCell(value: string | number | null | undefined) {
	// csvText neutralises spreadsheet formulas in guest-controlled cells (name/phone/email/notes).
	return `"${csvText(String(value ?? "")).replace(/\r?\n/g, " ").replace(/"/g, '""')}"`;
}

export function downloadCsv(filename: string, csv: string) {
	const element = document.createElement("a");
	const file = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
	const url = URL.createObjectURL(file);
	element.href = url;
	element.download = filename;
	document.body.appendChild(element);
	element.click();
	document.body.removeChild(element);
	window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function assignLanes(
	items: T.Schema.Booking.BookingSchema[],
	startMinuteFn: (b: T.Schema.Booking.BookingSchema) => number,
	durationMinsFn: (b: T.Schema.Booking.BookingSchema) => number
): Record<string, number> {
	const intervals = items
		.map((b) => ({
			id: b._id,
			start: startMinuteFn(b),
			end: startMinuteFn(b) + durationMinsFn(b),
		}))
		.sort((a, b) => a.start - b.start);
	const laneEnds: number[] = [];
	const out: Record<string, number> = {};
	for (const iv of intervals) {
		let L = 0;
		while (L < laneEnds.length && laneEnds[L] > iv.start) L++;
		if (L === laneEnds.length) laneEnds.push(iv.end);
		else laneEnds[L] = iv.end;
		out[iv.id] = L;
	}
	return out;
}

function slotOverlapsBooking(
	b: T.Schema.Booking.BookingSchema,
	slotStartMins: number,
	slotEndMins: number,
	startHour: number,
	tz: string,
	durationMinsFn: (b: T.Schema.Booking.BookingSchema) => number
): boolean {
	const m = moment(b.config.timestamp).tz(tz);
	const bStart = m.hours() * 60 + m.minutes() - startHour * 60;
	const bEnd = bStart + durationMinsFn(b);
	return slotStartMins < bEnd && slotEndMins > bStart;
}

/** Cloudtable-style: slot is free on this table for the moving booking’s duration. */
export function isTableSlotAvailable(
	tableId: string,
	slotStartMins: number,
	durationMins: number,
	items: T.Schema.Booking.BookingSchema[],
	ignoreBookingId: string | undefined,
	startHour: number,
	tz: string,
	durationMinsFn: (b: T.Schema.Booking.BookingSchema) => number,
	opts?: {
		day: Moment;
		tableBlocks?: TableBookingBlockLike[];
	}
): boolean {
	const slotEndMins = slotStartMins + durationMins;
	for (const b of items) {
		if (b._id === ignoreBookingId) continue;
		if (!b.table_ids?.includes(tableId)) continue;
		if (b.status === "cancelled" || b.status === "complete") continue;
		if (
			slotOverlapsBooking(
				b,
				slotStartMins,
				slotEndMins,
				startHour,
				tz,
				durationMinsFn
			)
		) {
			return false;
		}
	}
	if (opts?.day && opts.tableBlocks?.length) {
		const dayStr = opts.day.clone().tz(tz).format(FORMATS.moment.date);
		const blocked = buildBlockedByTableFromBlocks(
			filterTableBlocksForDay(opts.tableBlocks, dayStr),
			tz
		);
		const dayStart = opts.day.clone().tz(tz).startOf("day");
		const slotStartMs = dayStart
			.clone()
			.add(startHour * 60 + slotStartMins, "minutes")
			.valueOf();
		const slotEndMs = slotStartMs + durationMins * 60_000;
		if (
			tableBlockedForWindow(
				tableId,
				{ start: slotStartMs, end: slotEndMs },
				blocked
			)
		) {
			return false;
		}
	}
	return true;
}

/** Pixel placement for a table block strip on the day timeline. */
export function tableBlockTimelinePx(args: {
	block: TableBookingBlockLike;
	tz: string;
	startHour: number;
	minuteSize: number;
}): { left: number; width: number } | null {
	const iv = blockToIntervalMs(args.block, args.tz);
	if (!iv) return null;
	const startM = moment(iv.start).tz(args.tz);
	const endM = moment(iv.end).tz(args.tz);
	const startMin = startM.hours() * 60 + startM.minutes() - args.startHour * 60;
	const endMin = endM.hours() * 60 + endM.minutes() - args.startHour * 60;
	if (endMin <= 0) return null;
	const left = Math.max(0, startMin * args.minuteSize);
	const width = Math.max(4, (endMin - Math.max(0, startMin)) * args.minuteSize);
	return { left, width };
}

function mergeAdjacentPx(
	segs: Array<{ left: number; width: number }>
): Array<{ left: number; width: number }> {
	if (!segs.length) return [];
	const sorted = [...segs].sort((a, b) => a.left - b.left);
	const out: Array<{ left: number; width: number }> = [];
	let cur = { ...sorted[0] };
	for (let i = 1; i < sorted.length; i++) {
		const s = sorted[i];
		if (s.left <= cur.left + cur.width + 0.5) {
			cur.width = Math.max(cur.left + cur.width, s.left + s.width) - cur.left;
		} else {
			out.push(cur);
			cur = { ...s };
		}
	}
	out.push(cur);
	return out;
}

/** Grey stripes for timeline areas outside configured table-booking / location hours. */
export function buildClosedTimelineSegmentsPx(args: {
	day: Moment;
	tz: string;
	startHour: number;
	totalMinutes: number;
	minuteSize: number;
	slotStepMins: number;
	openWindowsMs: Array<{ start: number; end: number }>;
}): Array<{ left: number; width: number }> {
	const step = Math.max(5, Math.min(args.slotStepMins, 30));
	const raw: Array<{ left: number; width: number }> = [];
	let cursor = 0;
	const dayStart = args.day.clone().tz(args.tz).startOf("day");
	while (cursor < args.totalMinutes) {
		const segMins = Math.min(step, args.totalMinutes - cursor);
		const absStartMin = args.startHour * 60 + cursor;
		const absEndMin = absStartMin + segMins;
		const tsStart = dayStart.clone().add(absStartMin, "minutes").valueOf();
		const tsEnd = dayStart.clone().add(absEndMin, "minutes").valueOf();
		const open = isBookingIntervalWithinOpenHours(args.openWindowsMs, tsStart, tsEnd);
		if (!open) {
			raw.push({
				left: cursor * args.minuteSize,
				width: segMins * args.minuteSize,
			});
		}
		cursor += segMins;
	}
	return mergeAdjacentPx(raw);
}

export type BookingJoinRowRole = "single" | "primary" | "secondary";

export function bookingJoinRowRole(
	booking: { table_ids?: string[] },
	rowTableId: string
): BookingJoinRowRole {
	const ids = booking.table_ids;
	if (!ids || ids.length <= 1) return "single";
	return ids[0] === rowTableId ? "primary" : "secondary";
}

function pickBestComboIncludingAnchor(
	options: Array<{ table_ids: string[] }>,
	anchorTableId: string,
	party: number,
	tables: BookingTableWithJoin[]
): string[] | null {
	const candidates = options.filter((o) => o.table_ids.includes(anchorTableId));
	if (!candidates.length) return null;
	let best = candidates[0];
	let bestCount = best.table_ids.length;
	let bestExcess =
		best.table_ids.reduce(
			(s, id) => s + dineInTableCapacity(tables.find((t) => t._id === id) || {}, 4),
			0
		) - party;
	for (let i = 1; i < candidates.length; i++) {
		const o = candidates[i];
		const count = o.table_ids.length;
		const excess =
			o.table_ids.reduce(
				(s, id) => s + dineInTableCapacity(tables.find((t) => t._id === id) || {}, 4),
				0
			) - party;
		if (count < bestCount || (count === bestCount && excess < bestExcess)) {
			best = o;
			bestCount = count;
			bestExcess = excess;
		}
	}
	return best.table_ids;
}

/** True when this table row can anchor a single table or joined placement for the party. */
export function canTableRowFitParty(
	anchorTableId: string,
	party: number,
	tables: BookingTableWithJoin[]
): boolean {
	const row = tables.find((t) => t._id === anchorTableId);
	if (!row) return false;
	if (dineInTableCapacity(row, 4) >= party) return true;
	const options = listMoveTableOptions(
		tables,
		party,
		{ start: 0, end: 86_400_000 },
		new Map(),
		4,
		false
	);
	return options.some((o) => o.table_ids.includes(anchorTableId));
}

export function resolveCalendarPlacementTableIds(args: {
	anchorTableId: string;
	party: number;
	bookingId: string;
	existingTableIds?: string[];
	tables: BookingTableWithJoin[];
	items: T.Schema.Booking.BookingSchema[];
	slotStartMs: number;
	durationMins: number;
	turnMinutes: number;
	tz: string;
	dayDateStr: string;
	tableBlocks?: TableBookingBlockLike[];
}): string[] | null {
	const occupied = buildOccupiedByTableForAssignment(
		args.items,
		args.tableBlocks,
		args.turnMinutes,
		args.tz,
		args.dayDateStr,
		args.bookingId
	);
	const window = {
		start: args.slotStartMs,
		end: args.slotStartMs + args.durationMins * 60_000,
	};

	if (
		args.existingTableIds &&
		args.existingTableIds.length > 1 &&
		args.existingTableIds.includes(args.anchorTableId) &&
		validateExplicitTableAssignment(
			args.existingTableIds,
			args.tables,
			args.party,
			window,
			occupied
		)
	) {
		return [...args.existingTableIds];
	}

	const options = listMoveTableOptions(
		args.tables,
		args.party,
		window,
		occupied,
		4,
		false
	);
	return pickBestComboIncludingAnchor(
		options,
		args.anchorTableId,
		args.party,
		args.tables
	);
}

export function isCalendarPlacementSlotValid(args: {
	anchorTableId: string;
	party: number;
	bookingId: string;
	existingTableIds?: string[];
	tables: BookingTableWithJoin[];
	items: T.Schema.Booking.BookingSchema[];
	day: Moment;
	tz: string;
	startHour: number;
	slotMins: number;
	durationMins: number;
	turnMinutes: number;
	tableBlocks?: TableBookingBlockLike[];
}): boolean {
	const dayDateStr = args.day.clone().tz(args.tz).format(FORMATS.moment.date);
	const slotStartMs = args.day
		.clone()
		.tz(args.tz)
		.startOf("day")
		.add(args.startHour * 60 + args.slotMins, "minutes")
		.valueOf();
	return (
		resolveCalendarPlacementTableIds({
			anchorTableId: args.anchorTableId,
			party: args.party,
			bookingId: args.bookingId,
			existingTableIds: args.existingTableIds,
			tables: args.tables,
			items: args.items,
			slotStartMs,
			durationMins: args.durationMins,
			turnMinutes: args.turnMinutes,
			tz: args.tz,
			dayDateStr,
			tableBlocks: args.tableBlocks,
		}) !== null
	);
}

/** Matches cloudtable `placementDetails`: requested window vs next-free suggested window. */
export function computePlacementHighlight(
	mb: T.Schema.Booking.BookingSchema,
	ctx: {
		day: Moment;
		startHour: number;
		totalMinutes: number;
		tz: string;
		tables: T.Schema.Restaurant.Services.DineInTable[];
		items: T.Schema.Booking.BookingSchema[];
		durationMinsFn: (b: T.Schema.Booking.BookingSchema) => number;
		slotIntervalMins: number;
		turnMinutes: number;
		tableBlocks?: TableBookingBlockLike[];
	}
): { reqMins: number; dur: number; isOriginalSlotFree: boolean; suggestedMins: number } {
	const step = Math.max(1, ctx.slotIntervalMins);
	const m = moment(mb.config.timestamp).tz(ctx.tz);
	const reqMins = m.hours() * 60 + m.minutes() - ctx.startHour * 60;
	const dur = ctx.durationMinsFn(mb);
	const suitableTables = ctx.tables.filter((tbl) =>
		canTableRowFitParty(tbl._id, mb.config.number_of_people, ctx.tables)
	);
	const isFreeAt = (tblId: string, startM: number) =>
		isCalendarPlacementSlotValid({
			anchorTableId: tblId,
			party: mb.config.number_of_people,
			bookingId: mb._id,
			existingTableIds: mb.table_ids,
			tables: ctx.tables,
			items: ctx.items,
			day: ctx.day,
			tz: ctx.tz,
			startHour: ctx.startHour,
			slotMins: startM,
			durationMins: dur,
			turnMinutes: ctx.turnMinutes,
			tableBlocks: ctx.tableBlocks,
		});
	const isOriginalSlotFree = suitableTables.some((tbl) => isFreeAt(tbl._id, reqMins));
	let suggestedMins = reqMins;
	if (!isOriginalSlotFree) {
		let offset = step;
		while (offset < ctx.totalMinutes) {
			if (suitableTables.some((tbl) => isFreeAt(tbl._id, reqMins + offset))) {
				suggestedMins = reqMins + offset;
				break;
			}
			if (suitableTables.some((tbl) => isFreeAt(tbl._id, reqMins - offset))) {
				suggestedMins = reqMins - offset;
				break;
			}
			offset += step;
		}
	}
	return { reqMins, dur, isOriginalSlotFree, suggestedMins };
}
